import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Send, MapPin, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
    const { t } = useLang();
    const [params] = useSearchParams();
    const initialMode = params.get("type") === "test-ride" ? "test" : "contact";
    const bikeId = params.get("bike") || "";

    const [mode, setMode] = useState(initialMode);
    const [bikes, setBikes] = useState([]);

    const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [testForm, setTestForm] = useState({
        name: "",
        email: "",
        phone: "",
        bike_id: bikeId,
        bike_name: "",
        preferred_date: "",
        notes: "",
    });

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/bikes").then((r) => setBikes(r.data || []));
    }, []);

    useEffect(() => {
        if (bikeId) {
            const b = bikes.find((x) => x.id === bikeId);
            if (b) setTestForm((f) => ({ ...f, bike_id: b.id, bike_name: b.name }));
        }
    }, [bikes, bikeId]);

    const submitContact = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", contactForm);
            toast.success(t.forms.success);
            setContactForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const submitTest = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/test-ride", testForm);
            toast.success(t.forms.success);
            setTestForm({ name: "", email: "", phone: "", bike_id: "", bike_name: "", preferred_date: "", notes: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-20">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-x-20 lg:gap-y-4">

                    {/* TITLE — order-1 mobile, col1 row1 desktop */}
                    <div className="order-1 lg:col-start-1 lg:row-start-1 lg:self-start">
                        <p className="text-overline">{t.sections.contact_title}</p>
                        <h1 className="mt-3 font-display text-4xl font-bold tracking-tighter leading-[0.95] sm:text-5xl lg:text-6xl">
                            {t.sections.contact_title}
                        </h1>
                    </div>

                    {/* FORM CARD — order-2 mobile, col2 rows1-2 desktop */}
                    <div className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-24">
                        <div className="glass border border-black/10 p-5 lg:p-8">

                            {/* Tabs */}
                            <div className="flex border-b border-black/10" data-testid="contact-tabs">
                                <button
                                    onClick={() => setMode("contact")}
                                    data-testid="tab-contact"
                                    className={`px-4 py-3 text-xs uppercase tracking-[0.25em] transition-colors ${
                                        mode === "contact"
                                            ? "text-neutral-900 border-b-2"
                                            : "text-neutral-500 hover:text-neutral-900"
                                    }`}
                                    style={{ borderColor: mode === "contact" ? "var(--ls-blue)" : "transparent" }}
                                >
                                    {t.forms.contact_title}
                                </button>
                                <button
                                    onClick={() => setMode("test")}
                                    data-testid="tab-test-ride"
                                    className={`px-4 py-3 text-xs uppercase tracking-[0.25em] transition-colors ${
                                        mode === "test"
                                            ? "text-neutral-900 border-b-2"
                                            : "text-neutral-500 hover:text-neutral-900"
                                    }`}
                                    style={{ borderColor: mode === "test" ? "var(--ls-blue)" : "transparent" }}
                                >
                                    {t.forms.test_ride_title}
                                </button>
                            </div>

                            {mode === "contact" ? (
                                <form onSubmit={submitContact} className="mt-5 space-y-3" data-testid="contact-form">
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label={t.forms.name} value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} required testid="contact-name" />
                                        <Field label={t.forms.email} type="email" value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} required testid="contact-email" />
                                        <Field label={t.forms.phone} value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} testid="contact-phone" />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">
                                            {t.forms.message}
                                        </label>
                                        <textarea
                                            value={contactForm.message}
                                            onChange={(e) => setContactForm((f) => ({ ...f, message: e.target.value }))}
                                            rows={3}
                                            required
                                            data-testid="contact-message"
                                            className="bg-transparent border-b border-black/20 py-2 text-sm text-neutral-900 focus:outline-none focus:border-[--ls-blue] resize-none"
                                        />
                                    </div>
                                    <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="contact-submit-btn">
                                        <Send size={14} />
                                        {submitting ? t.forms.sending : t.forms.send}
                                    </button>
                                </form>
                            ) : (
                                <form onSubmit={submitTest} className="mt-5 space-y-3" data-testid="testride-form">
                                    <div className="grid grid-cols-2 gap-3">
                                        <Field label={t.forms.name} value={testForm.name} onChange={(e) => setTestForm((f) => ({ ...f, name: e.target.value }))} required testid="testride-name" />
                                        <Field label={t.forms.email} type="email" value={testForm.email} onChange={(e) => setTestForm((f) => ({ ...f, email: e.target.value }))} required testid="testride-email" />
                                        <Field label={t.forms.phone} value={testForm.phone} onChange={(e) => setTestForm((f) => ({ ...f, phone: e.target.value }))} required testid="testride-phone" />
                                        <Field label={t.forms.preferred_date} type="date" value={testForm.preferred_date} onChange={(e) => setTestForm((f) => ({ ...f, preferred_date: e.target.value }))} testid="testride-date" />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">
                                            {t.forms.select_bike}
                                        </label>
                                        <select
                                            value={testForm.bike_id}
                                            onChange={(e) => {
                                                const b = bikes.find((x) => x.id === e.target.value);
                                                setTestForm((f) => ({ ...f, bike_id: e.target.value, bike_name: b ? b.name : "" }));
                                            }}
                                            data-testid="testride-bike-select"
                                            className="bg-transparent border-b border-black/20 py-2 text-sm text-neutral-900 focus:outline-none focus:border-[--ls-blue]"
                                        >
                                            <option value="" className="bg-[--ls-bg]">—</option>
                                            {bikes.map((b) => (
                                                <option key={b.id} value={b.id} className="bg-[--ls-bg]">
                                                    {`${b.name} · ${b.year}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">
                                            {t.forms.notes}
                                        </label>
                                        <textarea
                                            value={testForm.notes}
                                            onChange={(e) => setTestForm((f) => ({ ...f, notes: e.target.value }))}
                                            rows={2}
                                            data-testid="testride-notes"
                                            className="bg-transparent border-b border-black/20 py-2 text-sm text-neutral-900 focus:outline-none focus:border-[--ls-blue] resize-none"
                                        />
                                    </div>
                                    <button type="submit" disabled={submitting} className="btn-accent w-full justify-center" data-testid="testride-submit-btn">
                                        <Send size={14} />
                                        {submitting ? t.forms.sending : t.forms.send}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* INFO — order-3 mobile, col1 row2 desktop */}
                    <div className="order-3 lg:col-start-1 lg:row-start-2">
                        <p className="text-sm text-neutral-600 leading-relaxed">
                            {t.sections.contact_desc}
                        </p>
                        <div className="mt-8 space-y-4 border-t border-black/10 pt-6">
                            <Info icon={MapPin} href="https://maps.app.goo.gl/yZKUXKpj4gAj7nuy7" external text={t.footer.address} />
                            <Info icon={Phone} href="tel:+34871005111" text={t.footer.phone} />
                            <Info icon={Mail} href="mailto:info@ls39.com" text="info@ls39.com" />
                            <Info icon={Clock} text={t.footer.hours} />
                        </div>
                        <div className="mt-8 aspect-video w-full overflow-hidden border border-black/10">
                            <iframe
                                title="map"
                                src="https://www.openstreetmap.org/export/embed.html?bbox=2.6%2C39.55%2C2.7%2C39.62&amp;layer=mapnik"
                                className="h-full w-full"
                                style={{ filter: "invert(0.92) hue-rotate(180deg)" }}
                            />
                        </div>
                    </div>

                </div>
            </section>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required = false, testid }) {
    return (
        <div className="flex flex-col gap-1">
            <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">{label}</label>
            <input
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={testid}
                className="bg-transparent border-b border-black/20 py-2 text-sm text-neutral-900 focus:outline-none focus:border-[--ls-blue]"
            />
        </div>
    );
}

function Info({ icon: Icon, text, href, external }) {
    const inner = (
        <span className="flex items-start gap-3 text-sm text-neutral-600">
            <Icon size={15} className="mt-0.5 shrink-0" style={{ color: "var(--ls-terracotta)" }} />
            <span>{text}</span>
        </span>
    );
    if (href) return (
        <a href={href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="block hover:text-neutral-900">
            {inner}
        </a>
    );
    return <div>{inner}</div>;
}
